import { z } from "zod";

import { LIMITS } from "../constants";

/**
 * The building blocks both sides validate with. The server enforces them in `validate()`;
 * the client runs the same schema before sending, so a user sees the error inline rather
 * than as a 400 from the network.
 */

export const emailSchema = z
  .email()
  .trim()
  .max(LIMITS.emailMax)
  .transform((value) => value.toLowerCase());

// Length over composition rules. The upper bound only keeps argon2 from being handed a
// megabyte of input.
export const passwordSchema = z.string().min(LIMITS.passwordMin).max(LIMITS.passwordMax);

export const nameSchema = z.string().trim().min(LIMITS.nameMin).max(LIMITS.nameMax);

/**
 * The identifier a client holds for any addressable resource: the row's UUIDv7 primary
 * key, serialized as-is.
 *
 * Validating it matters more than it looks. Postgres rejects a malformed uuid at the type
 * level (SQLSTATE 22P02), so an unchecked `:id` parameter turns a typo in a URL into a 500
 * instead of the 404 it should be. Parsing here keeps that a client error.
 *
 * No version constraint: the format is what protects the query, and pinning v7 would only
 * buy a different error code for an id that was never going to match a row anyway.
 */
export const resourceIdSchema = z.uuid();

export const slugSchema = z
  .string()
  .trim()
  .min(2)
  .max(48)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and single hyphens.");

/**
 * Whether a value is a same-origin relative path, safe to append to the app's origin.
 *
 * Checking prefixes alone is not enough: the WHATWG URL parser strips tabs and newlines
 * anywhere in the input and reads a backslash as a slash, so "/\t/evil.example" and
 * "/\\evil.example" both resolve to a different host. Rejecting control characters and
 * backslashes outright leaves "//" as the only way to name a host, and that is refused too.
 */
export const isRelativePath = (value: string): boolean =>
  value.startsWith("/") &&
  !value.startsWith("//") &&
  !value.includes("\\") &&
  // oxlint-disable-next-line no-control-regex -- matching control characters is the point.
  !/[\u0000-\u001f\u007f]/.test(value);

/**
 * A post-sign-in destination. Only a same-origin relative path is accepted: taking an
 * arbitrary URL would turn a sign-in link into an open redirect, where a legitimate-looking
 * email lands the user on a phishing page.
 */
export const redirectPathSchema = z
  .string()
  .max(512)
  .refine(isRelativePath, "Must be a relative path.");

export const paginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(LIMITS.pageSizeMax).default(LIMITS.pageSizeDefault),
  cursor: z.string().optional(),
});
